/* Shared primitives — no app state lives here. */

export const $ = (s, r = document) => r.querySelector(s);
export const $$ = (s, r = document) => [...r.querySelectorAll(s)];
/* the one HTML escaper — app.js, invites.js and the landing all use it */
export const esc = (s) =>
  String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
  );

/* media queries, read once; under node (the unit tests import the room's pure modules through here) there is no matchMedia */
const mq = (q) => (typeof matchMedia === 'function' ? matchMedia(q).matches : false);
export const RM = mq('(prefers-reduced-motion: reduce)');
/* A HANDHELD: a phone or a tablet — the primary input is a finger and nothing hovers. It is the one question the site asks
   about the DEVICE (never about the width: a narrow desktop window is still a desktop): can this visitor install Miya
   desktop from here? The share pages pick their door by it (Send Miya to my computer), js/core/track.js reports it as
   `platform`. Layout is the width's business alone (css media queries). */
export const HANDHELD = mq('(hover: none) and (pointer: coarse)');
/* the SCREEN's short side (the device, never the window: Split View and Stage Manager do not change it, a rotation does not
   swap it); 0 under node, where the unit tests import this file */
const SHORT = typeof screen === 'object' && screen ? Math.min(screen.width, screen.height) : 0;
/* A PHONE: a handheld whose screen is phone-sized — it gets the phone face (docs/phone/PHONE-PORT.md §2.1). A tablet keeps the
   desktop site (owner 2026-09-27). 600 is Android's own phone/tablet line; the biggest phones are ~440 on the short side, the
   smallest iPad (mini) is 744. An iPad with a trackpad still reports hover:none (WebKit), so it is a HANDHELD, not a PHONE.
   In a dev build `?face=phone|desktop` forces one (never in production). */
const FACE =
  typeof location === 'object' && location && import.meta.env?.DEV
    ? new URLSearchParams(location.search).get('face') || sessionFace()
    : '';
function sessionFace() {
  try {
    return sessionStorage.getItem('miya.face') || '';
  } catch {
    return '';
  }
}
if (FACE)
  try {
    sessionStorage.setItem('miya.face', FACE);
  } catch {
    /* no storage: the address carries it */
  }
/* a phone's own browser that says so (Android's "Mobile" token, the iPhone): the media queries are the rule, this is the net
   under them — the Android emulator's Chrome reports pointer:fine + hover (its mouse), and so may a phone with a mouse or a
   stylus paired (measured 2026-09-27, Pixel 3a API 34 emulator) */
const MOBILE_UA =
  typeof navigator === 'object' && !!navigator && /Android.+Mobile|iPhone|iPod/.test(navigator.userAgent || '');
/* written out again for the build's <head> preloads (vite.config.ts PHONE_JS: the front door's and the room's): change both */
export const PHONE = FACE ? FACE === 'phone' : (HANDHELD || MOBILE_UA) && SHORT > 0 && SHORT < 600;
/* an iPad in Safari's desktop mode (it sends the Mac user agent): only the tracking's os and the download door read it */
export const IPAD =
  typeof navigator === 'object' && !!navigator && /Mac/.test(navigator.platform || '') && navigator.maxTouchPoints > 1;

export const mmss = (s) => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
/* a length as m:ss, rounded and never negative (a record's or a list's duration; mmss floors — the playhead) */
export const clock = (sec) => {
  sec = Math.max(0, Math.round(sec || 0));
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
};
export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
/* the thread's time label */
export const hhmm = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/* Cached style writer — skips the DOM when the value hasn't changed, so an
   idle page performs ZERO style writes per frame (springs settle → strings
   stabilise → every sw() call becomes a no-op). */
export function sw(el, prop, val) {
  const c = el.__sw || (el.__sw = {});
  if (c[prop] === val) return;
  c[prop] = val;
  el.style[prop] = val;
}

/* Modal focus discipline — a dialog that owns the screen must own the Tab
   key too, and hand focus back where it came from when it leaves. Returns
   the release function; call it when the dialog closes. */
const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),' + 'select,textarea,[tabindex]:not([tabindex="-1"])';
/* `firstSel` picks where focus lands. Omit it and focus goes to the DIALOG
   itself when the dialog can take it (tabindex="-1") — never to its primary
   button: the keystroke that opened the dialog can carry through and press
   whatever is focused when it arrives, which turned an Enter in the composer
   into an immediate trip to X, past the consent line nobody got to read. */
/* copy to the clipboard, the old-school fallback included — true when it took */
export async function copyText(s) {
  try {
    await navigator.clipboard.writeText(s);
    return true;
  } catch {}
  const ta = document.createElement('textarea');
  ta.value = s;
  ta.setAttribute('readonly', '');
  ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {}
  ta.remove();
  return ok;
}
/* the screen reader's line (#srsay) and the in-place receipt on a button (width pinned so the row never jumps;
   the original label comes back after ms). The room's invites pile deals it. */
export function announce(msg) {
  const el = $('#srsay');
  if (el) el.textContent = msg;
}
const shown = new WeakMap();
export function receipt(btn, html, ms = 2000) {
  if (!btn) return;
  let st = shown.get(btn);
  if (!st) {
    st = { html: btn.innerHTML, t: 0 };
    const w = btn.getBoundingClientRect().width;
    if (w) btn.style.minWidth = Math.ceil(w) + 'px';
    shown.set(btn, st);
  } else clearTimeout(st.t);
  btn.innerHTML = html;
  btn.classList.add('said');
  announce(btn.textContent.trim());
  const restore = () => {
    clearTimeout(st.t);
    if (!shown.has(btn)) return;
    btn.innerHTML = st.html;
    btn.classList.remove('said');
    btn.style.minWidth = '';
    shown.delete(btn);
  };
  st.t = setTimeout(restore, ms);
  return restore;
}

export function trapFocus(el, firstSel) {
  const opener = document.activeElement;
  const visible = () => [...el.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null);
  const onKey = (e) => {
    if (e.key !== 'Tab') return;
    const f = visible();
    if (!f.length) return;
    const first = f[0],
      last = f[f.length - 1];
    /* focus outside the dialog (or at an edge) wraps back inside */
    if (!el.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
      return;
    }
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  document.addEventListener('keydown', onKey, true);
  const first = firstSel ? el.querySelector(firstSel) : el.hasAttribute('tabindex') ? el : visible()[0];
  first?.focus();
  return () => {
    document.removeEventListener('keydown', onKey, true);
    if (opener && document.contains(opener)) opener.focus();
  };
}

/* Seeded RNG — the wall scatter must look identical on every load so it can
   be art-directed (and individually overridden) with confidence. */
export function mulberry32(a) {
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* Under-damped spring (ported from miya_frontend miya-cat/springs.ts).
   k = stiffness, d = damping. step() integrates explicitly per frame. */
export class Spring {
  constructor(x, k, d) {
    this.x = x;
    this.t = x;
    this.v = 0;
    this.k = k;
    this.d = d;
  }
  set(t) {
    this.t = t;
  }
  kick(v) {
    this.v += v;
  }
  step(dt) {
    this.v += (-this.k * (this.x - this.t) - this.d * this.v) * dt;
    this.x += this.v * dt;
  }
}
